"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InputDataTab = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Copyright 2025 Scott Bender (scott@scottbender.net)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const react_1 = require("react");
const reactstrap_1 = require("reactstrap");
const InputDataTab = ({ pgnData, onCopyInput, isEditing = false, onInputChange }) => {
    const [editingText, setEditingText] = (0, react_1.useState)(() => (pgnData.input || []).join('\n'));
    const [hasChanges, setHasChanges] = (0, react_1.useState)(false);
    // Update editingText when pgnData changes (i.e., when a different PGN is selected)
    (0, react_1.useEffect)(() => {
        const newText = (pgnData.input || []).join('\n');
        setEditingText(newText);
        setHasChanges(false);
    }, [pgnData.input]);
    const handleTextChange = (0, react_1.useCallback)((event) => {
        const newText = event.target.value;
        setEditingText(newText);
        setHasChanges(true);
    }, []);
    const handleSave = (0, react_1.useCallback)(() => {
        if (onInputChange) {
            const newInputLines = editingText.split('\n').filter((line) => line.trim() !== '');
            onInputChange(newInputLines);
            setHasChanges(false);
        }
    }, [editingText, onInputChange]);
    const handleCancel = (0, react_1.useCallback)(() => {
        setEditingText((pgnData.input || []).join('\n'));
        setHasChanges(false);
    }, [pgnData.input]);
    const tabHeader = () => {
        return ((0, jsx_runtime_1.jsxs)("small", { children: [(0, jsx_runtime_1.jsx)("strong", { children: "PGN:" }), " ", pgnData.pgn, " |", (0, jsx_runtime_1.jsx)("strong", { children: " Source:" }), " ", pgnData.src, " |", (0, jsx_runtime_1.jsx)("strong", { children: " Destination:" }), ' ', pgnData.dst, (0, jsx_runtime_1.jsx)("br", {}), (0, jsx_runtime_1.jsx)("strong", { children: "Description:" }), " ", pgnData.description || 'N/A'] }));
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.CardHeader, { className: "d-flex justify-content-between align-items-center", children: [tabHeader(), (0, jsx_runtime_1.jsxs)("div", { children: [isEditing && hasChanges && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Button, { size: "sm", color: "success", onClick: handleSave, title: "Save changes", className: "me-2", children: "Save" }), (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { size: "sm", color: "secondary", onClick: handleCancel, title: "Cancel changes", className: "me-2", children: "Cancel" })] })), (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { size: "sm", color: "secondary", onClick: onCopyInput, title: "Copy input data to clipboard", children: "Copy" })] })] }), (0, jsx_runtime_1.jsx)("div", { className: "p-3", children: isEditing ? ((0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: "textarea", value: editingText, onChange: handleTextChange, style: {
                        fontFamily: 'monospace',
                        minHeight: '300px',
                        whiteSpace: 'pre',
                        wordBreak: 'break-all',
                    }, placeholder: "Enter input data, one line per entry..." })) : ((0, jsx_runtime_1.jsx)("div", { style: { fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }, children: (pgnData.input || []).map((input, index) => ((0, jsx_runtime_1.jsx)("div", { style: { marginBottom: '1px' }, children: input }, index))) })) })] }));
};
exports.InputDataTab = InputDataTab;
